<template>
  <label
    :for="name"
    :class="{ error: error && Object.keys(error).length > 0 }"
    class="bh-label"
    :style="{ paddingTop: label && !labelExistButNotVisible ? '30px' : '0' }"
  >
    <Transition>
      <span
        v-if="label && labelIsVisible"
        :class="['bh-label-tip', additionalStylesForLabel, { 'sr-only': labelExistButNotVisible }]"
      >
        {{ label }}
      </span>
    </Transition>
    <div class="input-wrapper" :class="{ 'has-toggle': isPasswordType }">
      <input
        :id="name"
        v-model="internalValue"
        :type="computedType"
        :name="name || 'content'"
        :placeholder="placeholder"
        :autocomplete="autocomplete"
        :disabled="disabled"
        :class="{ error: error && Object.keys(error).length > 0 }"
        :min="minValue"
        :style="{ height, width }"
        :aria-invalid="hasError ? 'true' : undefined"
        :aria-errormessage="hasError ? errorId : undefined"
        :aria-describedby="describedBy"
        @keydown.enter="$emit('enter', $event)"
        @click="$emit('clearErrors')"
        @focus="toggleLabel(true)"
        @blur="onBlur"
      />
      <button
        v-if="isPasswordType"
        type="button"
        class="password-toggle"
        :aria-label="isPasswordVisible ? 'Hide password' : 'Show password'"
        @click="togglePasswordVisibility"
      >
        <svg
          v-if="isPasswordVisible"
          class="password-toggle__icon"
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
          aria-hidden="true"
        >
          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
          <circle cx="12" cy="12" r="3" />
        </svg>
        <svg
          v-else
          class="password-toggle__icon"
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
          aria-hidden="true"
        >
          <path d="M17.94 17.94A10 10 0 0 1 12 20c-7 0-11-8-11-8a18.5 18.5 0 0 1 5.06-5.94" />
          <path d="M9.9 4.24A10 10 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
          <line x1="1" y1="1" x2="23" y2="23" />
        </svg>
      </button>
    </div>
    <Transition>
      <HasError
        v-if="error && Object.keys(error).length > 0"
        :key="`hasError-${name}`"
        :error="error"
        :field-id="name"
      />
    </Transition>
  </label>
</template>

<script lang="ts">
  import type { PropType } from 'vue'
  import { CommonErrorI } from '@/typescript/types/abstracts/errors/ErrorHandlerTypes'
  import { defineComponent } from 'vue'

  export default defineComponent({
    name: 'InputItem',
    props: {
      modelValue: {
        type: [String, Number],
        default: null,
      },
      name: {
        type: String,
        default: null,
      },
      error: {
        type: [Object, String] as PropType<CommonErrorI | string | undefined | null>,
        default: () => ({}),
      },
      label: {
        type: String,
        default: '',
      },
      placeholder: {
        type: String,
        default: '',
      },
      disabled: {
        type: Boolean,
        default: false,
      },
      type: {
        type: String,
        default: '',
      },
      height: {
        type: String,
        default: '',
      },
      width: {
        type: String,
        default: '',
      },
      isAlwaysShowLabel: {
        type: Boolean,
        default: false,
      },
      labelExistButNotVisible: {
        type: Boolean,
        default: false,
      },
      autocomplete: {
        type: String,
        default: 'on',
      },
      minValue: {
        type: Number,
        default: 0,
      },
      additionalStylesForLabel: {
        type: [String, Array, Object],
        default: '',
      },
    },
    emits: ['clearErrors', 'update:modelValue', 'enter', 'blur'],
    data() {
      return {
        labelIsVisible: false,
        isPasswordVisible: false,
      }
    },
    computed: {
      internalValue: {
        get() {
          return this.modelValue
        },
        set(v: any) {
          this.$emit('update:modelValue', v)
        },
      },
      isPasswordType(): boolean {
        return this.type === 'password'
      },
      computedType(): string {
        if (this.isPasswordType) {
          return this.isPasswordVisible ? 'text' : 'password'
        }
        return this.type || 'text'
      },
      hasError(): boolean {
        const e = this.error as any
        return !!(e && (e.hasError || e.message || Object.keys(e).length))
      },
      errorId(): string | undefined {
        return this.name ? `${this.name}-error` : undefined
      },
      describedBy(): string | undefined {
        const ids: string[] = []
        if (this.hasError && this.errorId) ids.push(this.errorId)
        return ids.length ? ids.join(' ') : undefined
      },
    },
    mounted() {
      if (this.isAlwaysShowLabel) {
        this.labelIsVisible = true
      }
    },
    methods: {
      toggleLabel(val: boolean) {
        if (!this.isAlwaysShowLabel) {
          this.labelIsVisible = val
        }
      },
      onBlur(event: FocusEvent) {
        this.toggleLabel(false)
        this.$emit('blur', event)
      },
      togglePasswordVisibility() {
        this.isPasswordVisible = !this.isPasswordVisible
      },
    },
  })
</script>

<style scoped lang="scss">
  input {
    border: 1px solid var(--color-text-grey);
    background-color: var(--color-light-grey);
    border-radius: 6px;
    color: var(--color-text-4);
    padding: 0 10px;
    height: 32px;
    box-sizing: border-box;
    position: relative;
    z-index: 3;
    width: 100%;
    transition: all 0.3s ease;
    font-family: var(--font-source-sans);

    &::placeholder {
      color: var(--color-placeholder-grey);
      opacity: 1 !important;
      font-size: 1em;
      height: 1.2em;
      line-height: 1.2em;
      font-weight: 400;
      transition: color 0.2s ease;
    }

    &:focus,
    &:focus-within,
    &:focus-visible {
      outline: none;
      border-color: var(--color-link-blue);
      background-color: var(--color-background-input);

      &::-webkit-input-placeholder {
        color: transparent;
      }
    }

    &[disabled] {
      background-color: var(--color-background-grey-1);
    }

    &.error {
      border-color: var(--color-border-error);
      background-color: var(--color-background-error);
    }

    &::-webkit-outer-spin-button,
    &::-webkit-inner-spin-button {
      -webkit-appearance: none;
      margin: 0;
    }
  }

  .bh-label {
    position: relative;
    display: block;
  }
  .bh-label-tip {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    font-size: 16px;
    font-style: normal;
    font-weight: 500;
    line-height: 24px;
    max-height: 20px;
    overflow: hidden;

    &__additional {
      font-weight: 600;
      font-size: 14px;
    }
  }
  .sr-only {
    position: absolute !important;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0 0 0 0);
    clip-path: inset(50%);
    border: 0;
    white-space: nowrap;
  }

  .input-wrapper {
    position: relative;
    display: flex;
    align-items: center;

    input {
      flex: 1;
    }

    &.has-toggle input {
      padding-right: 40px;
    }
  }

  .password-toggle {
    position: absolute;
    right: 8px;
    top: 50%;
    transform: translateY(-50%);
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 6px;
    background: none;
    border: none;
    border-radius: var(--bf-r-md);
    color: var(--bf-dusky-2);
    cursor: pointer;
    z-index: 4;
    transition:
      color var(--bf-dur-fast) var(--bf-ease),
      background-color var(--bf-dur-fast) var(--bf-ease);

    &:hover {
      color: var(--bf-orange-dark);
      background-color: var(--bf-nav-active);
    }

    &:focus-visible {
      outline: 2px solid var(--bf-orange-light);
      outline-offset: 2px;
    }
  }

  .password-toggle__icon {
    display: block;
    width: 18px;
    height: 18px;
  }
</style>
